import { getLocalTimeZone, now, parseAbsolute } from "@internationalized/date"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { MembershipMini } from "ipctg-inflow-types/InFlowDB"
import { GridList, GridListItem } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Card from "@/globalComponents/atoms/Card"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import EnterAppUserGroupForm from "@/globalComponents/molecules/EnterAppUserGroupForm"
import { formatDateForDisplay, getLocale } from "@/utilities/etc"
import {
  applyAccessCodeMutationOptions,
  membershipQueryOptions,
} from "@/utilities/hooks/queries/memberships"
import { useTheme } from "@/utilities/hooks/useTheme"
import { Crumb } from "@/utilities/types/local"

import styles from "./index.module.css"

export const Route = createFileRoute("/_authenticated/account/memberships/")({
  staticData: {
    pageName: "Keys & Memberships",
  },
  loader: () => ({
    crumb: { translationKey: "common:keysAndMemberships.header" } as Crumb,
  }),
  component: MembershipsRouteComponent,
})

export function MembershipsRouteComponent() {
  const { t, i18n } = useTranslation(["common"])
  const queryClient = useQueryClient()
  const { locationInformation } = useTheme()
  const locationID = locationInformation?.locationID || null
  const timeZone = locationInformation?.timeZone || getLocalTimeZone()
  const { data: memberships } = useSuspenseQuery(
    membershipQueryOptions({ locationID })
  )
  const sortedMemberships = sortExpiredMembershipsLast(memberships, timeZone)

  return (
    <>
      <Card>
        <Text level={1}>{t("keysAndMemberships.description")}</Text>
        <EnterAppUserGroupForm
          mutationOptions={applyAccessCodeMutationOptions(
            queryClient,
            locationID
          )}
        />
      </Card>
      {memberships.length === 0 ? (
        <Text
          level={2}
          className="center"
          style={{ color: "var(--neutral-11)" }}
        >
          {t("keysAndMemberships.emptyState")}
        </Text>
      ) : (
        <GridList
          items={sortedMemberships}
          className={styles.membershipList}
          aria-label={t("keysAndMemberships.header")}
        >
          {(membership) => {
            const endZdt = parseAbsolute(membership.membershipEndDate, timeZone)
            const neverExpires = endZdt.year >= 3000
            const expired = isExpired(membership, timeZone)
            const formatDate = (isoDateString: string) =>
              formatDateForDisplay({
                isoDateString,
                locale: i18n.resolvedLanguage || getLocale(),
                timeZone,
              })
            return (
              <GridListItem
                id={membership.membershipID}
                textValue={membership.appUserGroupName}
                className={styles.membershipItem}
                {...(expired ? { "data-expired": true } : {})}
              >
                <div className={styles.membershipIcon}>
                  <Icon name={expired ? "lock" : "key"} />
                </div>
                <div>
                  <Text className="bold">{membership.appUserGroupName}</Text>
                  <Text level={2}>
                    {t("keysAndMemberships.startDate", {
                      date: formatDate(membership.membershipStartDate),
                    })}
                  </Text>
                  <Text level={2}>
                    {neverExpires
                      ? t("keysAndMemberships.noExpiration")
                      : t("keysAndMemberships.endDate", {
                          date: formatDate(membership.membershipEndDate),
                        })}
                  </Text>
                </div>
              </GridListItem>
            )
          }}
        </GridList>
      )}
    </>
  )
}

function isExpired(membership: MembershipMini, timeZone: string) {
  return (
    now(timeZone).compare(
      parseAbsolute(membership.membershipEndDate, timeZone)
    ) >= 0
  )
}

function sortExpiredMembershipsLast(
  memberships: MembershipMini[],
  timeZone: string
) {
  return [...memberships].sort((a, b) => {
    const aExpired = isExpired(a, timeZone)
    const bExpired = isExpired(b, timeZone)
    if (aExpired !== bExpired) return Number(aExpired) - Number(bExpired)

    const endDateComparison = parseAbsolute(
      a.membershipEndDate,
      timeZone
    ).compare(parseAbsolute(b.membershipEndDate, timeZone))

    // Active: soonest-expiring first. Expired: most-recently-expired first.
    return aExpired ? -endDateComparison : endDateComparison
  })
}
